<template>
  <va-modal
    :value="isOpen"
    :noOutsideDismiss="true"
    ref="createOrUpdateEntity"
    :okText="$t('modals.buttons.save')"
    :cancelText="$t('modals.buttons.back')"
    @ok="submit"
    :ok-disabled="isSubmiting"
    @cancel="onCancel"
  >

    <div slot="header" class="modal-title">
      <span >{{ isUpdate ? $t('entities.edit_entity', {name: this.entity.name}) : $t('entities.add_entity') }}</span>
    </div>

    <form class="evollu-form">

      <!-- Entity Icon Input -->
      <div class="row">
        <div class="file-upload__container mx-3" @click.prevent="uploadIcon">
          <div class="file-upload__inner">
            <div v-if="previewLogo" class="file-upload__preview">
              <img class="entity-logo" :src="previewLogo" />
                <div class="file-upload__action" @click.stop="deleteIcon">
                  <i class="fa fa-trash"></i>
                </div>
            </div>
            <div v-else class="d-flex flex-column justify-content-center align-items-center">
              <a href="#"><span class="fa fa-upload mr-1"></span></a>
              <span class="file-upload__main">{{$t('entities.input.upload_icon')}}</span>
            </div>
          </div>
        </div>
        <div class="text-danger" v-if="form.errors.has('icon')">
          <small>
            {{$t(form.errors.get('lang'))}}
          </small>
        </div>
      </div>

      <input ref="uploadIconInput" id="upload-icon-input" type="file" @input="entityIconSelected" hidden accept=".png, .jpg, .jpeg, .gif">

      <!-- Entity Name Input -->
      <div class="row">
        <div class="col-12">
          <va-input
            id="name-input"
            v-model="form.data.name"
            :placeholder="$t('entities.inputs.name')"
            :error="form.errors.has('name')"
            :error-messages="[$t(form.errors.get('name'))]"
            @focus="form.errors.clear('name')"
            />
        </div>
      </div>

      <!-- Entity NIF Input -->
      <div class="row">
        <div class="col-12">
          <va-input
            id="nif-input"
            v-model="form.data.nif"
            :placeholder="$t('entities.inputs.nif')"
            :error="form.errors.has('nif')"
            :error-messages="[$t(form.errors.get('nif'))]"
            @focus="form.errors.clear('nif')"
            />
        </div>
      </div>

      <!-- Entity Facebook Input -->
      <div class="row">
        <div class="col-4">
          <va-input
            id="facebook-id-input"
            v-model="form.data.facebook_id"
            placeholder="Facebook"
            :error="form.errors.has('facebook_id')"
            :error-messages="[$t(form.errors.get('facebook_id'))]"
            @focus="form.errors.clear('facebook_id')"
            />
        </div>

        <div class="col-4">
          <va-input
            id="twitter-id-input"
            v-model="form.data.twitter_id"
            placeholder="Twitter"
            :error="form.errors.has('twitter_id')"
            :error-messages="[$t(form.errors.get('twitter_id'))]"
            @focus="form.errors.clear('twitter_id')"
            />
        </div>

        <div class="col-4">
          <va-input
            id="linkedin-id-input"
            v-model="form.data.linkedin_id"
            placeholder="LinkedIn"
            :error="form.errors.has('linkedin_id')"
            :error-messages="[$t(form.errors.get('linkedin_id'))]"
            @focus="form.errors.clear('linkedin_id')"
            />
        </div>
      </div>

      <!-- Input Entity Country -->
      <div class="row">
        <div class="col-12">
          <label class="form-label">{{$t('patients.inputs.address.country')}}</label>
          <multiselect
            id="country-multiselect"
            v-model="form.data.country"
            :options="countries"
            track-by="code"
            label="name"
            select-label=""
            deselect-label="×"
            open-direction="bellow"
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            @input="form.errors.clear('country')"
            >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
          </multiselect>
          <div class="text-danger" v-if="form.errors.has('country')">
            <small>
              {{$t(form.errors.get('country'))}}
            </small>
          </div>
        </div>
      </div>

      <!-- Entity Language Input -->
      <div class="row">
        <div class="col-12">
            <label class="form-label">{{$t('entities.inputs.language')}}</label>
            <multiselect
              id="language-multiselect"
              v-model="form.data.lang"
              :options="languages"
              track-by="id"
              :custom-label="formatLanguageLabel"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              @input="form.errors.clear('lang')"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('lang')">
              <small>
                {{$t(form.errors.get('lang'))}}
              </small>
            </div>
        </div>
      </div>

      <div class="row" v-if="hasWriteStatusPermissionToEntity">
        <div class="col-12 status-multiselect-wrapper">
          <label class="form-label" for="status-multiselect">{{$t('entities.inputs.status')}}</label>
          <multiselect
            id="status-multiselect"
            v-model="form.data.status"
            :options="statuses"
            track-by="id"
            label="name"
            select-label=""
            deselect-label="×"
            open-direction="bellow"
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            @input="form.errors.clear('status')"
            >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
            <template slot="option" slot-scope="props">
              <span>{{ props.option.name }}</span>
              <span v-if="props.option.warning" class="status-warning">
                <b>{{ props.option.warning }}</b>&nbsp;{{ props.option.desc }}&nbsp;&nbsp;<span :class="props.option.icon"></span>
              </span>
            </template>
          </multiselect>
          <div class="text-danger" v-if="form.errors.has('status')">
            <small>
              {{$t(form.errors.get('status'))}}
            </small>
          </div>
        </div>
      </div>

      <div v-if="isFromEvollu">

        <!-- Entity Users Limit Input -->
        <div class="row">
          <div class="col-12">
            <label for="users-limit-slider" class="form-label mb-3">{{ $t('entities.inputs.users_limit') }}</label>
            <va-slider
              value-visible
              v-model="form.data.users_limit"
              :min="5"
              :max="50"
            />
          </div>
        </div>

        <!-- Input Aud Provider -->
        <div class="row">
          <div class="col-12">
            <label class="form-label">{{$t('entities.inputs.aud_provider')}}</label>
            <multiselect
              id="aud-provider-multiselect"
              v-model="form.data.aud_provider"
              :options="availableAudProviders"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              @input="form.errors.clear('aud_provider')"
              >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('aud_provider')">
              <small>
                {{$t(form.errors.get('aud_provider'))}}
              </small>
            </div>
          </div>
        </div>

        <!-- Input Screening Provider -->
        <!--<div class="row" v-if="!isUpdate || (isUpdate && !availableScreeningProviders.some(provider => provider.id === entity.id))">
          <div class="col-12">
            <label class="form-label">{{$t('entities.inputs.screening_providers')}}</label>
            <multiselect
              id="promoter-provider-multiselect"
              v-model="form.data.screening_providers"
              :options="availableScreeningProviders"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="true"
              :multiple="true"
              :searchable="true"
              :close-on-select="false"
              @input="form.errors.clear('screening_providers')"
              >
              <template slot="clear">
                <span class="multiselect__clear" v-if="Array.isArray(form.data.screening_providers) && form.data.screening_providers.length" @mousedown.prevent.stop="form.data.screening_providers = []"></span>
              </template>
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('screening_providers')">
              <small>
                {{$t(form.errors.get('screening_providers'))}}
              </small>
            </div>
          </div>
        </div>-->

        <!--Workactivities provided by the entity itself-->
        <div class="row">
          <div class="col-12">
            <label class="form-label">{{$t('entities.activities.activities_provided_by_self_entity')}}</label>
            <multiselect
              id="aud-provider-multiselect"
              v-model="form.data.activities_provided_by_self"
              :options="activitiesOptions"
              track-by="id"
              label="name"
              :custom-label="opt => $t(`entities.activity_types.${opt.id}`)"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="true"
              :multiple="true"
              :searchable="true"
              @input="form.errors.clear('activities_provided_by_self')"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('activities_provided_by_self')">
              <small>
                {{$t(form.errors.get('activities_provided_by_self'))}}
              </small>
            </div>
          </div>
        </div>

        <div class="col-12 col-md-6" v-if="hasWriteStatusPermissionToEntity">
          <span class="select is-fullwidth is-small">
            <va-checkbox
              :label="`${$t('entities.inputs.audiologyService')}?`"
              id="checkbox-is-audiology-service"
              :isCircle="true"
              v-model="form.data.is_audiology_service"/>
          </span>
        </div>

        <div class="row" v-if="hasWriteStatusPermissionToEntity">
          <div class="col-12">
            <label class="form-label">{{$t('entities.inputs.products')}}</label>
            <multiselect
              id="product-multiselect"
              v-model="form.data.product"
              :options="productsOptions"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              @input="form.errors.clear('product_id')"
            >
              <template slot="clear">
                <span class="multiselect__clear" v-if="form.data.product" @mousedown.prevent.stop="form.data.product = null"></span>
              </template>
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('product_id')">
              <small>
                {{$t(form.errors.get('product_id'))}}
              </small>
            </div>
          </div>
        </div>

        <div class="row" v-if="hasWriteStatusPermissionToEntity">
          <div class="col-12">
            <label class="form-label">{{$t('entities.inputs.business_plan')}}</label>
            <multiselect
              id="business-plan-multiselect"
              v-model="form.data.business_plan"
              :options="businessPlans"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              :disabled="!form.data.product"
              @input="form.errors.clear('business_plan_id')"
              >
              <template slot="clear">
                <span class="multiselect__clear" v-if="form.data.business_plan" @mousedown.prevent.stop="form.data.business_plan = null"></span>
              </template>
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('business_plan_id')">
              <small>
                {{$t(form.errors.get('business_plan_id'))}}
              </small>
            </div>
          </div>
        </div>
        <div class="row" v-if="hasWriteStatusPermissionToEntity && form.data.product && form.data.product.name === 'Hear' && ( form.data.business_plan && (form.data.business_plan.id === 5 || form.data.business_plan.id === 7))">
          <div class="col-12">
            <va-input
              label="FTE"
              id="fte-input"
              type="number"
              v-model.number="form.data.fte"
              placeholder="FTE"
              :error="form.errors.has('fte')"
              :error-messages="[$t(form.errors.get('fte'))]"
              @focus="form.errors.clear('fte')"
            />
          </div>
        </div>
      </div>
    </form>
  </va-modal>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Multiselect from 'vue-multiselect'
import Errors from '@/utils/CustomNodeErrors.js'
import {EntityService, ProductsService, ActivityService} from '@/api/AuthService'
import CountryList from '@/data/CorrectedCountryList'
import { arraysAreEqual } from '@/utils/functions'
import IsFromEvolluMixin from '@/mixins/IsFromEvolluMixin'
import CurrentLocaleMixin from '@/mixins/CurrentLocaleMixin'
import * as countries from 'i18n-iso-countries'

import Vue from "vue";

const entitiesInfoType = 110
const entityStatusInfoType = 120
const readAccessType = 1
const writeAccessType = 2
const adminAccessType = 3

export default {
  props: {
    entity: {
      type: Object,
      required: false,
    }
  },
  components: {
    Multiselect
  },
  mixins: [
    IsFromEvolluMixin,
    CurrentLocaleMixin
  ],
  created: async function () {
    this.$store.dispatch('LOAD_BUSINESS_PLANS')
    let products = await ProductsService.getAll()
    this.productsOptions = products
    let activities = await ActivityService.query()
    activities.sort(function(a, b) {
      var textA = a.name.toUpperCase();
      var textB = b.name.toUpperCase();
      return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
    });
    this.activitiesOptions = activities
    if (this.isUpdate) {
      this.prepareForUpdate()
    } else {
      this.form.data.lang = this.languages.find(elem => elem.lang === this.locale)
    }
  },
  mounted: async function () {
    this.isOpen = true
  },
  watch: {
    'form.data.product': function(val) {
      if (val) {
        this.businessPlans = val.businessPlans
        if (this.entity.business_plan_id) {
          this.form.data.business_plan = this.businessPlans.find(plan => plan.id === this.entity.business_plan_id) || null
        }
      } else {
        this.businessPlans = []
        this.form.data.business_plan = null
      }
    }
  },
  computed: {
    ...mapState(['languages']),
    ...mapGetters([
      'availableAudProviders',
      'availableScreeningProviders',
    ]),
    isUpdate: function () {
      return this.entity !== null && this.entity !== undefined
    },
    /*countries: function () {
      for (let code of this.$store.state.countries.allowed) {
        if (code.toLowerCase() === "gb") {
          code = "en"
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${code.toLowerCase()}.json`))
      }
      let tempCountries = countries.getNames(Vue.i18n.locale().toLowerCase() === 'gb' ? 'en' : Vue.i18n.locale().toLowerCase())
      let countriesList = []
      for(const key in tempCountries) {
        countriesList.push({
          name: tempCountries[key],
          code: key
        })
      }
      return countriesList
      //return CountryList.filter(country => this.$store.state.countries.allowed.includes(country.code))
      //return CountryList
    },*/
    countries: function () {
      for (let code of this.$store.state.countries.allowed) {
        if (code.toLowerCase() === "gb") {
          code = "en"
        } else if (code.toLowerCase() === 'cz') {
          code = 'cs'
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${code.toLowerCase()}.json`))
      }
      let vueI18nLocale = Vue.i18n.locale().toLowerCase()
      switch (vueI18nLocale) {
        case 'gb':
          vueI18nLocale = 'en'
          break;
        case 'cz':
          vueI18nLocale = 'cs'
          break;
      }
      let tempCountries = countries.getNames(vueI18nLocale)
      let countriesList = []
      for(const key in tempCountries) {
        countriesList.push({
          name: tempCountries[key],
          code: key
        })
      }
      return countriesList
      //return CountryList.filter(country => this.$store.state.countries.allowed.includes(country.code))
      //return CountryList
    },
    authService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollauth_server) || null
    },
    readableEntities: function () {
      if (this.authService) {
        let permissions = this.authService.user_permissions.filter(p => p.info_type === entitiesInfoType && p.access_type >= readAccessType)
        if (permissions.some(p => p.entity_id === null)) {
          return [...this.authService.entities]
        } else {
          return this.authService.entities.filter(entity => permissions.some(p => p.entity_id === entity.id))
        }
      }
      return []
    },
    hasWriteStatusPermissionToEntity: function () {
      if (this.isFromEvollu) {
        if (this.entity) {
          return this.authService.user_permissions.some(permission => permission.info_type === entityStatusInfoType && permission.access_type === writeAccessType && (permission.entity_id === this.entity.id || permission.entity_id === null))
        } else {
          return this.authService.user_permissions.some(permission => permission.info_type === entityStatusInfoType && permission.access_type === writeAccessType && permission.entity_id === null)
        }
      }
      return false
    },
  },
  data: function () {
    return {
      isOpen: false,
      isSubmiting: false,
      previewLogo: null,
      statuses: [
        { id: 0, name: this.$t('entities.status.0'), warning: null },
        { id: 1, name: this.$t('entities.status.1'), warning: null },
        { id: 2, name: this.$t('entities.status.2'), warning: this.$t('entities.status.warning'), desc: this.$t('entities.status.warning_desc'), icon: 'i-evollu-warning' }
      ],
      shouldUpdateIcon: false,
      productsOptions: [],
      activitiesOptions: [],
      businessPlans: [],
      form: {
        data: {
          name: null,
          nif: null,
          icon: null,
          users_limit: 10,
          country: null,
          status: null,
          aud_provider: null,
          screening_providers: [],
          activities_provided_by_self: [],
          product: null,
          business_plan: null,
          facebook_id: null,
          twitter_id: null,
          linkedin_id: null,
          lang: null,
          fte: null,
          is_audiology_service: false
        },
        errors: new Errors(),
      },
    }
  },
  methods: {
    formatLanguageLabel: function (value) {
      let language = this.languages.find(elem => elem.lang === value.lang.toLowerCase())
      return `${language.emoji} ${this.$t('language.' + language.language_key)}`
    },
    uploadIcon: function () {
      this.$refs.uploadIconInput.click()
    },
    deleteIcon: function () {
      this.form.data.icon = null
      this.previewLogo = null
      this.shouldUpdateIcon = true
    },
    entityIconSelected: function (event) {
      let files = event.target.files || event.dataTransfer.files
      if (!files.length) {
        return
      }
      this.form.data.icon = files[0]
      this.previewLogo = URL.createObjectURL(this.form.data.icon)
      this.$refs.uploadIconInput.value = null
      this.shouldUpdateIcon = true
    },
    prepareForUpdate: function () {
      this.previewLogo = this.entity.icon_url
      this.form.data.name = this.entity.name
      this.form.data.linkedin_id = this.entity.linkedin_id
      this.form.data.facebook_id = this.entity.facebook_id
      this.form.data.twitter_id = this.entity.twitter_id
      this.form.data.nif = this.entity.nif
      if (this.entity.lang) {
        this.form.data.lang = this.languages.find(elem => elem.lang === this.entity.lang)
      } else {
        this.form.data.lang = this.languages.find(elem => elem.lang === this.locale)
      }
      this.form.data.status = this.statuses.find(status => status.id === this.entity.status) || null
      this.form.data.country = this.countries.find(country => country.code === this.entity.country) || null
      //this.form.data.country = CountryList.find(country => country.code === this.entity.country) || null
      this.form.data.users_limit = this.entity.users_limit
      if (this.entity.aud_provider_id) {
        this.form.data.aud_provider = this.readableEntities.find(e => e.id === this.entity.aud_provider_id) || null
      }
      if(this.entity.products && this.entity.products.length > 0) {
        this.form.data.product = this.productsOptions.find(prod => prod.id === this.entity.products[0].id) || null
      }
      if(this.entity.activity_providers && this.entity.activity_providers.length > 0) {
        const activitiesProvidedIds = this.entity.activity_providers.map(elem => elem.activity_id)
        if (activitiesProvidedIds.length > 0) {
          this.form.data.activities_provided_by_self = this.activitiesOptions.filter(elem => activitiesProvidedIds.includes(elem.id))
        }
      }
      if (this.entity.business_plan_id) {
        this.form.data.business_plan = this.businessPlans.find(plan => plan.id === this.entity.business_plan_id) || null
      }
      if (this.entity.icon) {
        this.previewLogo = this.entity.icon
        this.form.data.icon = this.entity.icon
      }
      if (this.entity.activity_providers && this.entity.activity_providers.length) {
        this.form.data.screening_providers = this.entity.activity_providers.filter(provider => provider.activity_id === 3).map(provider => provider.entity)
      }

      if(this.entity.fte) {
        this.form.data.fte = this.entity.fte || null
      }

      this.form.data.is_audiology_service = this.entity.is_audiology_service
    },
    submit: function () {
      this.submitForm()
        .then(entity => this.submitIcon(entity))
        .then(() => {
          this.$refs.createOrUpdateEntity.cancel()
        })
        .catch(errors => {
          // Errors have been handled before
        })
    },
    submitForm: function () {
      return new Promise((resolve, reject) => {
        this.isSubmiting = true
        if (this.isUpdate) {
          EntityService.update(this.entity.id, this.getFormData())
            .then(entity => {
              this.$store.dispatch('UPDATE_ENTITY', { entityId: this.entity.id, entity })
              this.$emit('entity-updated', { entity })
              this.showToast(this.$t('entities.messages.edit_success'), this.$store.state.successToastOptions)
              resolve(entity)
            })
            .catch(errors => {
              this.processServerErrors(errors)
              reject(errors)
            })
            .finally(() => {
              this.isSubmiting = false
            })
        } else {
          EntityService.store(this.getFormData())
            .then(entity => {
              this.$store.dispatch('NEW_ENTITY', { entity })
              this.$emit('entity-created', { entity })
              this.showToast(this.$t('entities.entity_created'), this.$store.state.successToastOptions)
              resolve(entity)
            })
            .catch(errors => {
              this.processServerErrors(errors)
              reject(errors)
            })
            .finally(() => {
              this.isSubmiting = false
            })
        }
      })
    },
    submitIcon: function (entity) {
      return new Promise((resolve, reject) => {
        if (this.shouldUpdateIcon) {
          let formData = new FormData()
          formData.append('icon', this.form.data.icon)
          EntityService.updateIcon(entity.id, formData)
            .then(entity => {
              this.$store.dispatch('UPDATE_ENTITY', { entityId: this.entity.id, entity })
              this.$emit('entity-updated', { entity })
              this.showToast(this.$t('entities.entity_icon_updated'), this.$store.state.successToastOptions)
              resolve()
            })
            .catch(errors => {
              this.processServerErrors(errors, 'entities.messages.file_upload_error')
              reject(errors)
            })
        } else {
          resolve()
        }
      })
    },
    processServerErrors: function (errors, defaultErrorMessage = 'entities.messages.error') {
      if (errors.response && errors.response.status && errors.response.status === 422) {
        this.form.errors.record(errors.response.data.errors, 'server_auth.errors.')
      } else {
        this.showToast(this.$t(defaultErrorMessage), this.$store.state.errorToastOptions)
      }
    },
    getFormData: function () {
      let data = {
        name: this.form.data.name,
        facebook_id: this.form.data.facebook_id,
        twitter_id: this.form.data.twitter_id,
        linkedin_id: this.form.data.linkedin_id,
        lang: this.form.data.lang.lang,
        nif: this.form.data.nif ? this.form.data.nif : null,
        users_limit: this.form.data.users_limit ? this.form.data.users_limit : null,
        product_id: this.form.data.product ? this.form.data.product.id : null,
        activities_provided_by_self_ids: this.form.data.activities_provided_by_self.length > 0 ? this.form.data.activities_provided_by_self.map(elem => elem.id) : [],
        fte: this.form.data.fte,
        business_plan_id: this.form.data.business_plan ? this.form.data.business_plan.id : null,
        is_audiology_service: this.form.data.is_audiology_service,
        ...(this.form.data.country && { country: this.form.data.country.code }),
        ...(this.form.data.status && { status: this.form.data.status.id }),
        ...(this.form.data.aud_provider && { aud_provider: this.form.data.aud_provider.id }),
      }

      const originalScreeningProviders = this.entity ? this.entity.activity_providers.filter(provider => provider.activity_id === 3).map(provider => provider.entity_id) : []
      const newScreeningProviders = this.form.data.screening_providers.map(element => element.id)

      if (!arraysAreEqual(originalScreeningProviders, newScreeningProviders)) {
        data.screening_providers = newScreeningProviders
      }
      return data
    },
    onCancel: function () {
      this.isOpen = false
      setTimeout(() => {
        this.$emit('cancel')
      }, 500)
    }
  }
}
</script>

<style lang="scss">

.status-multiselect-wrapper {
  .status-warning {
    display: flex;
    float: right;
    font-size: 90%;
    color: #c81414;
    b {
      font-weight: bold;
    }
  }
  .multiselect__option--highlight .status-warning {
    color: white;
    .i-evollu-warning {
      background: url('../../assets/icons/warning-white.svg') no-repeat center center;
    }
  }
}

.entity-logo {
  max-width:150px;
  max-height:90px;
  width:auto;
  height:auto;
}

.file-upload__container {
  display: flex;
  cursor: pointer;
  height: 8rem;
  width: 100%;
  background-color: whitesmoke;
  border: 2px dashed #23c9b4;
  border-radius: 10px;

  & .file-upload__preview {
    // margin-left: 2.5rem;
    // margin-top: 0.8rem;
    display: flex;
    align-items: center;
    justify-content: center;

    & .entity-logo {
      max-width:170px;
      max-height:80px;
      width:auto;
      height:auto;
    }

    & .file-upload__action {
      // margin-bottom: 1rem;
      width: 2rem;
      height: 2rem;
      background-color: #bfbfbf;
      margin-left: 1rem;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50%;

      & i {
        color: #7b7d7b;
      }
    }
  }

  & .file-upload__inner {
    display: flex;
    flex-direction: column;
    justify-content:center;
    width: 100%;
    align-items: center;

    & a {
      font-size: 1.65rem;
    }

    & .file-upload__main {
      color: #23c9b4;
      font-size: 1rem;
    }
  }
}

</style>
